import { Fragment, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Listbox, Transition } from '@headlessui/react'
import { ChevronRightIcon, CheckIcon } from 'components/icons'
import { countries } from 'countries-list'
import classNames from 'classnames'

const CountrySelectListbox = ({
  selectedCountry,
  setSelectedCountry,
  label,
  initialCountry,
  style = 'admin',
}) => {
  useEffect(() => {
    setSelectedCountry(Object.values(countries).find(country => country.name === initialCountry))
  }, [initialCountry])

  return (
    <Listbox value={selectedCountry} onChange={setSelectedCountry} >
      {({ open }) => (
        <div className="relative">
          <Listbox.Button className={classNames(
            'relative w-full py-2 pl-3 pr-10 text-left shadow-sm cursor-default h-50',
            {
              'ring-2 ring-primary ring-opacity-8 focus:ring-primary focus:ring-opacity-50 rounded-8': style === 'admin',
              'ring-input-border ring-1 rounded-4': style === 'customer',
            },
          )}>
            {
              selectedCountry
                ? <div className="flex flex-row items-center gap-10 ml-10 mr-20 text-grey-800">
                  <div>{selectedCountry.emoji}</div>
                  <div className="line-clamp-2">{selectedCountry.name}{selectedCountry.name === selectedCountry.native ? '' : ` - (${selectedCountry.native})`}</div>
                </div>
                : <span className="font-medium text-opacity-50 ml-15 text-grey-900 text-18">{label}</span>
            }
            <span className="absolute inset-y-0 flex items-center pr-2 ml-3 rotate-90 right-15">
              <ChevronRightIcon />
            </span>
          </Listbox.Button>
          <Transition
            show={open}
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options
              static
              className="absolute z-10 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-200 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
            >
              {Object.entries(countries)
                .sort((a, b) => a[1].name.localeCompare(b[1].name))
                .map(([countryCode, country]) => (
                  <Listbox.Option
                    key={'country-codes-' + countryCode}
                    className={({ active }) =>
                      classNames(
                        'cursor-default select-none relative py-2 pl-3 pr-9',
                        {
                          'text-primary bg-primary-light': active,
                          'text-grey': !active,
                        },
                      )
                    }
                    value={country}
                  >
                    {({ selected, active }) => (
                      <>
                        <div className="flex flex-row-reverse items-center justify-end gap-10 px-10 py-5">
                          <span className={classNames(
                            'ml-3 block',
                            {
                              'font-semibold text-primary': selected,
                              'font-normal': !selected,
                            },
                          )}>
                            {country.name}{country.name === country.native ? '' : ` - (${country.native})`}
                          </span>
                          <span>{country.emoji}</span>
                        </div>

                        {selected
                          ? (
                            <span className={classNames(
                              'absolute inset-y-0 right-0 flex items-center pr-4',
                              {
                                'text-primary': active,
                                'text-grey': !active,
                              },
                            )}>
                              <CheckIcon className="w-10 h-10" aria-hidden="true" />
                            </span>
                          )
                          : null}
                      </>
                    )}
                  </Listbox.Option>
                ))}
            </Listbox.Options>
          </Transition>
        </div>
      )
      }
    </Listbox >
  )
}

CountrySelectListbox.propTypes = {
  selectedCountry: PropTypes.object,
  setSelectedCountry: PropTypes.func,
  initialCountry: PropTypes.string,
  style: PropTypes.string,
  label: PropTypes.string,
}

export default CountrySelectListbox
